import React, { ComponentType } from "react";
import toast from "react-hot-toast";
import classNames from "classnames";
import moment from "moment";
import AsyncSelect from "react-select/async";
import { v4 as uuidv4 } from 'uuid';
import { useDebounce } from 'use-debounce';
import { toNString } from "../../../helpers/guids"
import { FileRejection, useDropzone } from "react-dropzone";
import { DropzoneBaseOptions } from "./MailMessageAttachmentsUploadForm";
import { components, MultiValue, OptionProps } from "react-select";
import { DocumentType } from "../../../types/DocumentType";
import { MailMessageUploadFormProps } from "./MailMessageAttachmentsUploadForm";
import { DocumentDetailsCard } from "../../utilities/DocumentDetailsCard";
import { useLazySearchMasterAccountDocumentsQuery } from "../../../services/clientdocuments";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getDocumentTypeFromMime } from "../../utilities/DocumentTypeIcon";

export type MailMessageAttachmentsFileInputProps = MailMessageUploadFormProps & {
    masterAccountId?: string | null | undefined
};

const DocumentSelectOption = (props: OptionProps<SelectOptionData<string, DocumentType>>) => {
    return (
        <components.Option {...props}>
            <DocumentDetailsCard 
                bordered={false}
                className="px-0"
                fileName={props.data.label}
                fileSize={props.data.data?.fileSize ?? 0}
                type={props.data.data?.documentType ?? -1}
            />
        </components.Option>
    );
};

export const MailMessageAttachmentsFileInput = ({ append, fields, masterAccountId, replace } : Readonly<MailMessageAttachmentsFileInputProps>) => {
    const componentId = React.useId();
    const [cacheKey, setCacheKey] = React.useState<number>(new Date().valueOf());
    const [__values__, setValues] = React.useState<SelectOptionData<string, DocumentType>[]>([]);
    const [values] = useDebounce(__values__, 300);
    const [load] = useLazySearchMasterAccountDocumentsQuery();

    // function to convert the result from the graph service into a format that react-select uses
    // to display the options correctly.
    const buildOptions = (data: DocumentType[]) : SelectOptionData<string, DocumentType>[] => {
        return data.map((el) => ({
            label: el.fileName,
            value: el.documentId,
            isDisabled: false,
            data: el
        } as SelectOptionData<string, DocumentType>))
    };

    // used by the async-react-select component to fetch and display all the documents available
    // in the dropdown menu.
    const loadOptions = (input: string, callback: (options: SelectOptionData<string, DocumentType>[]) => void) : void => {
        if (!masterAccountId) {
            callback([]);
            return;
        }

        const newCacheKey = new Date().valueOf();
        load({ cacheKey: newCacheKey, masterAccountId, searchTerm: input, page: 1, limit: 50 })
            .then(
                ({ data }: { data: PagedResults<DocumentType> }) => {
                    if (data && data.results.length > 0) {
                        callback(buildOptions(data.results));
                    }
                    else {
                        callback([]);
                    }
                },
                () => {
                    toast.error('There was a problem fetching your results.');
                    callback([]);
                }
            )
            .finally(() => {
                setCacheKey(() => newCacheKey);
            });
    };

    // when the dropzone field gets/accepts files from the user, will validate the files and append them to our form array
    const onDrop = React.useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
        // notify the user of any failed files
        for (let i = 0; i < rejectedFiles.length; i++) {
            toast.error(`Could not attach file '${rejectedFiles[i].file.name}'. Reason: ${rejectedFiles[i].errors[0]}`);
        }
        // append all the files that our dropzone params have said that the file is acceptable to be uploaded
        append(acceptedFiles.map((el: File) => ({
            id: toNString(uuidv4()),
            file: el,
            documentId: null,
            type: 'Upload',
            name: el.name,
            size: el.size,
            fileType: getDocumentTypeFromMime(el.type),
            lastModified: el.lastModified,
            password: null
        } as MailMessageAttachment)))
    }, []);

    // triggers when the menu items change, we want to update the attachments list that we want to send
    // up with the email
    React.useEffect(() => {
        // Collect the selected document IDs from the select control
        const selectedDocIds = values.map((el) => el.value);

        // Step 1: Retain:
        // - Uploaded files (file present)
        // - Locked fields (__isLocked__)
        // - Documents still selected in the select input
        const retainedAttachments  = fields.filter((el) => 
            el.file 
            || el.__isLocked__ === true 
            || (el.documentId && selectedDocIds.includes(el.documentId))
        );

        // Step 2: Find documents from select not yet in attachments
        const retainedDocIds = retainedAttachments
            .filter((el) => el.documentId)
            .map((el) => el.documentId);

        const newAttachments = values
            .filter((el) => !retainedDocIds .includes(el.value))
            .map((el) => ({
                id: el.value,
                file: null,
                documentId: el.value,
                type: 'GraphDocument',
                name: el.data?.fileName,
                size: el.data?.fileSize,
                fileType: el.data?.documentType,
                lastModified: moment(el.data?.dateUploaded).valueOf(),
                password: null
            }) as MailMessageAttachment);

        // Step 3: Replace field array only if it actually changed
        const nextAttachments = [...retainedAttachments, ...newAttachments];

        const hasChanged =
            nextAttachments.length !== fields.length ||
            nextAttachments.some((att, i) => att.documentId !== fields[i]?.documentId);

        if (hasChanged) {
            replace(nextAttachments);
        }
    }, [ fields, values, replace ]);

    // provides two-way sync when the fields are update outwith the select (this) component
    React.useEffect(() => {
        // Get current document-based attachments (ignore file uploads and locked ones)
        const currentDocIds = new Set(
            fields
                .filter((el) => el.documentId && !el.file && el.__isLocked__ !== true)
                .map((el) => el.documentId)
         );

        // Filter the current select values to remove anything not in the field array
        const filteredValues = __values__.filter((el) => currentDocIds.has(el.value));

        // Only update if the filtered result is different
        const isEqual =
            filteredValues.length === __values__.length &&
            filteredValues.every((v, i) => v.value === __values__[i].value);

        if (!isEqual) {
            setValues(filteredValues);
        }
    }, [ fields ]);

    // do not allow a document search/select when the master account is not set
    // let the parent mail message 'watch' the form for the master-account-id and pass the value into this component
    // lets not create a dependency for the client context, etc and keep this flowing a generically as possible.
    const { getRootProps, getInputProps } = useDropzone({ ...DropzoneBaseOptions, onDrop });
    if (!masterAccountId) {
        return null;
    }

    return (
        <div className="input-group mb-2">
            <div {...getRootProps({ className: 'dropzone d-flex justify-content-center align-items-center btn btn-outline-secondary' })}>
                <input {...getInputProps()} multiple />
                <FontAwesomeIcon icon={['fas', 'cloud-upload-alt']} /><span className="ms-2">Upload</span>
            </div>
            <AsyncSelect 
                id={componentId}
                className={classNames('form-control border-0 p-0')}
                styles={{
                    control: (base, state) => ({
                        ...base,
                        borderTopLeftRadius: 'unset',
                        borderBottomLeftRadius: 'unset',
                        boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
                        borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
                        '&:hover': {
                            borderColor: state.isFocused ? '#86b7fe' : '#ced4da'
                        }
                    })
                }}
                closeMenuOnSelect={false}
                controlShouldRenderValue={false}
                cacheOptions={true}
                defaultOptions={true}
                isMulti={true}
                placeholder="Browse..."
                value={__values__}
                loadOptions={loadOptions as any}
                onChange={(opts: MultiValue<SelectOptionData<string, DocumentType>>) => setValues(() => [...opts])}
                components={{
                    Option: DocumentSelectOption
                }}
            />
        </div>
    );
};